import React, { useCallback, useEffect, useState } from "react";
import MypageLayout from "./MypageLayout";
import StatusBadge from "./StatusBadge";
import { emitToast } from "./Toast";
import ErrorBar from "./ErrorBar";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import ConfirmDialog from "./ConfirmDialog";
import Pager, { usePager } from "./Pager";
import { refreshBadgeCounts } from "./notifications";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 承認系ページ（休み申請・時間訂正・残業）の共通コンポーネント。
// config で 列/カード/API を差し替える。PC=テーブル / スマホ=カード / 承認・却下は確認ダイアログ経由。
// config: {
//   title, active, listPath, decidePath, emptyText?,
//   fields: [{ header, value:(row)=>node, nowrap?, muted?, wrap?, primary?, minWidth? }],  // primary=スマホカードの見出し(社員名) / minWidth=PC列の最小幅(px)
//   approvable?:(row)=>bool, rejectable?:(row)=>bool,
// }
const API_URL = process.env.REACT_APP_API_URL || "";

const FILTERS = [
  { key: "pending", text: "申請中" },
  { key: "approved", text: "承認済" },
  { key: "rejected", text: "却下" },
  { key: "all", text: "すべて" },
];

function ApprovalList({ config }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [decidingId, setDecidingId] = useState(null);
  // 承認/却下の確認ダイアログ（{ row, action }。null=閉じている）。誤操作防止。
  const [confirm, setConfirm] = useState(null);

  const approvable = config.approvable || ((r) => r.status !== "approved");
  const rejectable = config.rejectable || ((r) => r.status !== "rejected");
  const primaryField = config.fields.find((f) => f.primary) || config.fields[0];
  // 表示件数を超えたらページャーを出す（1ページぶんだけ描画＝件数が増えても重くならない）。
  const { page, setPage, pageRows, pageCount } = usePager(rows);

  const fetchList = useCallback(async (status) => {
    setLoading(true);
    setError("");
    try {
      const query = status === "all" ? "" : `?status=${encodeURIComponent(status)}`;
      const res = await fetch(`${API_URL}${config.listPath}/${query}`, { credentials: "include" });
      if (res.status === 401) { setError("ログインが必要です。"); return; }
      if (res.status === 403) { setError("管理者権限が必要です。"); return; }
      if (!res.ok) throw new Error("取得に失敗しました");
      const data = await res.json();
      setRows(data.results || []);
    } catch (e) {
      setError("申請一覧の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [config.listPath]);

  useEffect(() => { fetchList(filter); }, [filter, fetchList]);

  const doDecide = async () => {
    if (!confirm) return;
    const { row, action } = confirm;
    setDecidingId(row.id);
    setError("");
    try {
      const res = await fetch(`${API_URL}${config.decidePath}/`, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id: row.id, action }),
      });
      // BEの理由をそのまま出す（例:「申請中の申請のみ承認/却下できます」＝他の人が先に処理した）。
      if (!res.ok) { setError(await apiErrorMessage(res, "処理に失敗しました。")); return; }
      emitToast(action === "approve" ? "承認しました。" : "却下しました。");
      await fetchList(filter);
      // 承認待ちが1件減った＝サイドバーの勤怠履歴バッジも減らす（状態由来）。
      // AttendanceByDate の右パネルと同じ decide API なので、どちらの入口から承認しても
      // バッジの挙動を揃える。
      refreshBadgeCounts();
    } catch (e) {
      setError("処理に失敗しました。");
    } finally {
      setDecidingId(null);
      setConfirm(null);
    }
  };

  // 承認/却下ボタン（PC・スマホ共通）。どちらも不可なら「-」。
  const actionButtons = (r) => {
    const busy = decidingId === r.id;
    const ap = approvable(r);
    const rj = rejectable(r);
    if (!ap && !rj) return <span className="mp-muted">-</span>;
    return (
      <>
        {ap && <button className="mp-btn mp-accent mp-sm" disabled={busy} onClick={() => setConfirm({ row: r, action: "approve" })}>承認</button>}
        {rj && <button className="mp-btn mp-danger mp-sm" disabled={busy} onClick={() => setConfirm({ row: r, action: "reject" })}>却下</button>}
      </>
    );
  };

  const detailFields = config.fields.filter((f) => f !== primaryField);

  return (
    <MypageLayout active={config.active} title={config.title}>
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      <div className="mp-tabs">
        {FILTERS.map((f) => (
          <button key={f.key} className={`mp-tab${filter === f.key ? " mp-on" : ""}`} onClick={() => { setFilter(f.key); setPage(1); }}>{f.text}</button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <EmptyState>{config.emptyText || "申請がありません"}</EmptyState>
      ) : (
        <>
          {/* デスクトップ：テーブル */}
          <div className="mp-tablewrap mp-only-desktop">
            <table className="mp-table">
              <thead>
                <tr>
                  {config.fields.map((f) => <th key={f.header}>{f.header}</th>)}
                  <th>状態</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                    <tr key={r.id}>
                      {config.fields.map((f) => {
                        const cls = [f.nowrap ? "mp-nowrap" : "", f.muted ? "mp-muted" : ""].filter(Boolean).join(" ");
                        return (
                          <td key={f.header} className={cls || undefined} style={f.minWidth ? { minWidth: f.minWidth } : undefined}>
                            {f.wrap ? <div className="mp-cellwrap">{f.value(r)}</div> : f.value(r)}
                          </td>
                        );
                      })}
                      <td><StatusBadge status={r.status} /></td>
                      <td><div className="mp-actions">{actionButtons(r)}</div></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* モバイル：カード（社員名＋状態／各項目／承認・却下） */}
          <div className="mp-apv-list mp-only-mobile">
            {pageRows.map((r) => (
                <div className="mp-apv-item" key={r.id}>
                  <div className="mp-apv-head">
                    <span className="mp-apv-name">{primaryField.value(r)}</span>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="mp-apv-fields">
                    {detailFields.map((f) => (
                      <div className="mp-apv-row" key={f.header}>
                        <span className="mp-apv-label">{f.header}</span>
                        <span className="mp-apv-value">{f.value(r)}</span>
                      </div>
                    ))}
                  </div>
                  {(approvable(r) || rejectable(r)) ? (
                    <div className="mp-apv-actions">{actionButtons(r)}</div>
                  ) : null}
                </div>
              ))}
          </div>

          <Pager page={page} pageCount={pageCount} onChange={setPage} />
        </>
      )}

      {/* 承認/却下の確認ダイアログ（誤操作防止）。背景クリック / Esc / やめる で閉じる。 */}
      <ConfirmDialog
        open={!!confirm}
        ariaLabel="承認の確認"
        title={confirm && `この申請を${confirm.action === "approve" ? "承認" : "却下"}しますか？`}
        onClose={() => setConfirm(null)}
        onConfirm={doDecide}
        confirmLabel={confirm ? (decidingId === confirm.row.id ? "処理中…" : (confirm.action === "approve" ? "承認する" : "却下する")) : ""}
        confirmClass={confirm && confirm.action === "approve" ? "mp-accent" : "mp-danger"}
        confirmDisabled={confirm ? decidingId === confirm.row.id : false}
      >
        {confirm && `${primaryField.value(confirm.row)} さんの申請を${confirm.action === "approve" ? "承認" : "却下"}します。`}
      </ConfirmDialog>
    </MypageLayout>
  );
}

export default ApprovalList;
